import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, MapPin, FileText, Tag, FolderOpen, 
  Calendar, CheckSquare, File, Clock, LogOut, Search,
  ChevronLeft, ChevronRight, Menu, Settings, DollarSign, X, UserCheck
} from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import SearchResults from './SearchResults';
import { api } from '../utils/api';

const AppShell = ({ user, onLogout, children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const location = useLocation();
  const searchRef = useRef(null);

  const navItems = [
    { icon: Home, label: 'Dashboard', path: '/', dataTestId: 'left-rail-nav-dashboard-link' },
    { icon: MapPin, label: 'State Matrix', path: '/states', dataTestId: 'left-rail-nav-state-matrix-link' },
    { icon: FileText, label: 'Licenses', path: '/licenses', dataTestId: 'left-rail-nav-licenses-link' },
    { icon: Tag, label: 'DBAs', path: '/dbas', dataTestId: 'left-rail-nav-dbas-link' },
    { icon: FolderOpen, label: 'Ancillary Registrations', path: '/filings', dataTestId: 'left-rail-nav-filings-link' },
    { icon: UserCheck, label: 'Registered Agents', path: '/registered-agents', dataTestId: 'left-rail-nav-registered-agents-link' },
    { icon: DollarSign, label: 'Expense Tracker', path: '/expenses', dataTestId: 'left-rail-nav-expenses-link' },
    { icon: CheckSquare, label: 'Tasks & Calendar', path: '/tasks', dataTestId: 'left-rail-nav-tasks-link' },
    { icon: File, label: 'Documents', path: '/documents', dataTestId: 'left-rail-nav-documents-link' },
    { icon: Clock, label: 'Audit Log', path: '/audit', dataTestId: 'left-rail-nav-audit-link' },
  ];

  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setSearchLoading(true);
        try {
          const results = await api.search(searchQuery);
          setSearchResults(results);
          setShowResults(true);
        } catch (error) {
          console.error('Search failed:', error);
          setSearchResults(null);
        } finally {
          setSearchLoading(false);
        }
      } else {
        setSearchResults(null);
        setShowResults(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close search results and mobile menu when route changes
  useEffect(() => {
    setShowResults(false);
    setSearchQuery('');
    setMobileMenuOpen(false);
  }, [location]);

  const handleSearch = async (e) => {
    e.preventDefault();
    // Search is handled by the debounced effect
    // This just prevents form submission
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 md:hidden backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Close menu"
        />
      )}

      {/* Left Rail */}
      <aside 
        data-testid="left-rail"
        className={`${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 w-[280px] ${
          collapsed ? 'md:w-20' : 'md:w-64'
        } bg-card border-r border-border transition-all duration-300 ease-out flex flex-col fixed h-screen z-50 shadow-2xl md:shadow-none`}
      >
        {/* Header */}
        <div className="h-14 border-b border-border flex items-center justify-between px-4">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div>
                <h1 className="font-semibold text-base tracking-tight bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  North Bridge Solutions
                </h1>
                <p className="text-xs text-muted-foreground">Compliance Control</p>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="text-lg font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
              NB
            </div>
          )}
          
          {/* Desktop only: Collapse/Expand button - HIDDEN ON MOBILE */}
          <Button
            data-testid="left-rail-collapse-button"
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="hidden md:flex btn-hover"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
          
          {/* Mobile only: X Close button - HIDDEN ON DESKTOP */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(false)}
            className="flex md:hidden btn-hover"
            data-testid="mobile-sidebar-close"
            title="Close menu"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={item.dataTestId}
                className={`
                  flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg transition-colors
                  ${isActive 
                    ? 'bg-accent text-accent-foreground border-l-4 border-accent' 
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }
                `}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="border-t border-border p-4">
          {!collapsed ? (
            <div className="space-y-3">
              {/* User Info */}
              <div>
                <div className="text-sm font-medium truncate">{user.full_name}</div>
                <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                <Badge variant="secondary" className="text-xs mt-1">{user.role}</Badge>
              </div>
              
              {/* Settings & Logout Buttons */}
              <div className="flex gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.href = '/settings'}
                  className="btn-hover"
                  title="Settings"
                >
                  <Settings className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onLogout}
                  className="flex-1 btn-hover"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => window.location.href = '/settings'}
                className="w-full btn-hover"
                title="Settings"
              >
                <Settings className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onLogout}
                className="w-full btn-hover"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 ${collapsed ? 'md:ml-20' : 'md:ml-64'} ml-0 transition-all duration-300 min-w-0`}>
        {/* Top Bar */}
        <header data-testid="top-command-bar" className="h-14 border-b border-border bg-card/95 backdrop-blur sticky top-0 z-40">
          <div className="h-full px-4 md:px-6 flex items-center justify-between gap-2">
            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex-shrink-0"
              data-testid="mobile-menu-toggle"
              title={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              <Menu className="h-5 w-5" />
            </Button>

            <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-0">
              <div ref={searchRef} className="relative w-full md:w-80">
                <form onSubmit={handleSearch}>
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
                  <Input
                    data-testid="global-search-input"
                    type="text"
                    placeholder="Search licenses, filings, DBAs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
                    className="pl-10"
                  />
                </form>
                {showResults && searchResults && (
                  <SearchResults 
                    results={searchResults} 
                    onClose={() => setShowResults(false)}
                    searchQuery={searchQuery}
                  />
                )}
              </div>
            </div>

            <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
              <div className="text-xs text-muted-foreground">
                <span className="font-medium">Compliance Control</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-6 max-w-full overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppShell;
